import {useState, useRef} from 'react';
import {Button} from '@common-web/components/ui/button';
import {Progress} from '@common-web/components/ui/progress';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {Badge} from '@common-web/components/ui/badge';
import {Upload, File, X, Check, AlertCircle} from 'lucide-react';
import {trpc} from '@common-web/lib/trpc';
import {reportError} from '@common-web/lib/errorReporting';
import {useRfcContext} from '../RfcContext';
import {RfcAttachmentType} from '@common/types/rfc';
import {MAX_UPLOAD_BYTES} from '@common/constants/files';

interface FileUploadProps {
  fileType: RfcAttachmentType;
  onUpload?: (file: File) => void;
  onChange?: () => void; // Called after successful upload or delete
  accept?: string;
  maxSize?: number; // in bytes
  multiple?: boolean;
  existingFiles?: Array<{
    id: string;
    fileName: string;
    fileSize: number;
  }>;
}

interface UploadedFile {
  file: File;
  progress: number;
  status: 'uploading' | 'uploaded' | 'error';
  error?: string;
  attachmentId?: string;
}

export function FileUpload({
  fileType,
  onUpload,
  onChange,
  accept = '*/*',
  maxSize = MAX_UPLOAD_BYTES,
  multiple = false,
  existingFiles = [],
}: FileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [dragActive, setDragActive] = useState(false);

  const {accessToken, password} = useRfcContext();

  const generateUploadUrlMutation = trpc.clients.rfc.generateUploadUrl.useMutation();
  const confirmUploadMutation = trpc.clients.rfc.confirmUpload.useMutation();
  const deleteAttachmentMutation = trpc.clients.rfc.deleteAttachment.useMutation();

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file: File): string | null => {
    if (file.size > maxSize) {
      return `File size must be less than ${formatFileSize(maxSize)}`;
    }

    if (accept !== '*/*') {
      const allowedExtensions = accept.split(',').map((ext) => ext.trim());
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedExtensions.some((ext) => ext === fileExtension || ext === file.type)) {
        return `File type not allowed. Accepted types: ${accept}`;
      }
    }

    return null;
  };

  const uploadFile = async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setUploadedFiles((prev) => [
        ...prev,
        {
          file,
          progress: 0,
          status: 'error',
          error: validationError,
        },
      ]);
      return;
    }

    const newFile: UploadedFile = {
      file,
      progress: 0,
      status: 'uploading',
    };
    setUploadedFiles((prev) => [...prev, newFile]);

    try {
      const uploadData = await generateUploadUrlMutation.mutateAsync({
        accessToken,
        password,
        fileName: file.name,
        fileType,
        contentType: file.type,
        fileSize: file.size,
      });

      const uploadResponse = await fetch(uploadData.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      setUploadedFiles((prev) => prev.map((f) => (f.file === file ? {...f, progress: 100} : f)));

      const attachment = await confirmUploadMutation.mutateAsync({
        accessToken,
        password,
        fileName: file.name,
        fileType,
        fileSize: file.size,
        contentType: file.type,
        s3Key: uploadData.s3Key,
      });

      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.file === file
            ? {
                ...f,
                status: 'uploaded',
                attachmentId: attachment.id,
              }
            : f,
        ),
      );

      onUpload?.(file);
      onChange?.();
    } catch (error) {
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.file === file
            ? {
                ...f,
                status: 'error',
                error: error instanceof Error ? error.message : 'Upload failed',
              }
            : f,
        ),
      );
    }
  };

  const handleFileSelect = (files: FileList) => {
    const fileArray = Array.from(files);
    if (!multiple && fileArray.length > 1) {
      fileArray.splice(1);
    }

    fileArray.forEach(uploadFile);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const removeFile = async (uploadedFile: UploadedFile) => {
    if (uploadedFile.attachmentId) {
      try {
        await deleteAttachmentMutation.mutateAsync({
          accessToken,
          password,
          attachmentId: uploadedFile.attachmentId,
        });
        onChange?.();
      } catch (error) {
        reportError(error, {
          title: 'Could not delete file',
          context: 'FileUpload.removeFile',
        });
      }
    }

    setUploadedFiles((prev) => prev.filter((f) => f !== uploadedFile));
  };

  const removeExistingFile = async (fileId: string) => {
    try {
      await deleteAttachmentMutation.mutateAsync({
        accessToken,
        password,
        attachmentId: fileId,
      });
      onChange?.();
    } catch (error) {
      reportError(error, {
        title: 'Could not delete file',
        context: 'FileUpload.removeExistingFile',
      });
    }
  };

  return (
    <div className='space-y-4'>
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-muted-foreground/50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Upload className='mx-auto h-12 w-12 text-muted-foreground mb-4' />
        <div className='space-y-2'>
          <p className='text-lg font-medium'>
            Drag and drop files here, or{' '}
            <button
              type='button'
              className='text-primary hover:text-primary/80 underline'
              onClick={() => fileInputRef.current?.click()}
            >
              browse
            </button>
          </p>
          <p className='text-sm text-muted-foreground'>
            {accept !== '*/*' && `Accepted file types: ${accept}`}
            {accept !== '*/*' && maxSize && ' • '}
            {maxSize && `Max file size: ${formatFileSize(maxSize)}`}
          </p>
        </div>

        <input
          ref={fileInputRef}
          type='file'
          accept={accept}
          multiple={multiple}
          className='hidden'
          onChange={(e) => e.target.files && handleFileSelect(e.target.files)}
        />
      </div>
      {existingFiles.length > 0 && (
        <div className='space-y-2'>
          <h4 className='font-medium'>Uploaded Files</h4>
          {existingFiles.map((file) => (
            <div
              key={file.id}
              className='flex items-center justify-between p-3 bg-muted rounded-lg'
            >
              <div className='flex items-center space-x-3'>
                <File className='h-4 w-4 text-muted-foreground' />
                <div>
                  <p className='text-sm font-medium'>{file.fileName}</p>
                  <p className='text-xs text-muted-foreground'>{formatFileSize(file.fileSize)}</p>
                </div>
              </div>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => removeExistingFile(file.id)}
                disabled={deleteAttachmentMutation.isPending}
              >
                <X className='h-4 w-4' />
              </Button>
            </div>
          ))}
        </div>
      )}
      {uploadedFiles.length > 0 && (
        <div className='space-y-2'>
          <h4 className='font-medium'>Recent Uploads</h4>
          {uploadedFiles.map((uploadedFile, index) => (
            <div key={index} className='space-y-2'>
              <div className='flex items-center justify-between p-3 bg-muted rounded-lg'>
                <div className='flex items-center space-x-3 flex-1'>
                  <div className='shrink-0'>
                    {uploadedFile.status === 'uploaded' && (
                      <div className='w-6 h-6 bg-green-500 rounded-full flex items-center justify-center'>
                        <Check className='h-3 w-3 text-white' />
                      </div>
                    )}
                    {uploadedFile.status === 'error' && (
                      <div className='w-6 h-6 bg-red-500 rounded-full flex items-center justify-center'>
                        <AlertCircle className='h-3 w-3 text-white' />
                      </div>
                    )}
                    {uploadedFile.status === 'uploading' && (
                      <div className='w-6 h-6 bg-primary rounded-full animate-pulse' />
                    )}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium truncate'>{uploadedFile.file.name}</p>
                    <p className='text-xs text-muted-foreground'>
                      {formatFileSize(uploadedFile.file.size)}
                    </p>
                    {uploadedFile.status === 'uploading' && (
                      <Progress value={uploadedFile.progress} className='mt-1' />
                    )}
                    {uploadedFile.status === 'error' && (
                      <Alert className='mt-2'>
                        <AlertCircle className='h-4 w-4' />
                        <AlertDescription className='text-xs'>
                          {uploadedFile.error}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                  <Badge
                    variant={
                      uploadedFile.status === 'uploaded'
                        ? 'success'
                        : uploadedFile.status === 'error'
                          ? 'destructive'
                          : 'secondary'
                    }
                  >
                    {uploadedFile.status}
                  </Badge>
                </div>
                <Button variant='ghost' size='sm' onClick={() => removeFile(uploadedFile)}>
                  <X className='h-4 w-4' />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
